// A resource that genuinely failed before any network activity completed reports zero
// across the board: no bytes transferred or decoded, and no time elapsed between the
// browser giving up and it starting (fetchStart === responseEnd). A cache hit is NOT this
// shape — it still reports the resource's real decodedBodySize and some, even if tiny,
// duration — so this only matches hard failures (blocked, DNS/TLS failure, network error),
// not fast-but-successful loads.
export function isHardResourceFailure(entry: PerformanceResourceTiming) {
  return (
    entry.transferSize === 0 &&
    entry.decodedBodySize === 0 &&
    entry.duration === 0
  );
}

export interface FailedResourceSnapshot {
  url: string;
  initiatorType: string;
  responseStatus: number;
}

// Browsers only ever expose "this thing you asked for failed" to page JS — never *why*,
// and for anything with its own nested fetches (a CSS `@import`, an `@font-face` src, a
// worker's own imports) the visible failure is attributed to the outer resource, not
// whichever nested one actually broke. Resource Timing is a separate, independent record
// of every fetch the page made — including failed ones — so pulling recent hard failures
// out of it is the only way to recover the real culprit for Sentry, whatever asset type
// it turns out to be.
export function getRecentFailedResources(
  withinMs = 5000,
): FailedResourceSnapshot[] {
  const cutoff = performance.now() - withinMs;
  return (
    performance.getEntriesByType('resource') as PerformanceResourceTiming[]
  )
    .filter(
      (entry) => entry.startTime >= cutoff && isHardResourceFailure(entry),
    )
    .map((entry) => ({
      url: entry.name,
      initiatorType: entry.initiatorType,
      responseStatus: entry.responseStatus,
    }));
}
